/**
 * Project Manager Onboarding Component
 * Specialized onboarding flow for project managers
 */

import React, { useState } from 'react';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Grid,
    Alert,
    AlertTitle,
    Chip,
    Avatar,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    FormControlLabel,
    Checkbox,
    LinearProgress
} from '@mui/material';
import {
    Public,
    AttachMoney,
    Security,
    Schedule,
    CheckCircle,
    Info,
    Home,
    Phone,
    Email,
    LocationOn,
    SupervisorAccount,
    Engineering,
    Business,
    VerifiedUser
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FileUpload from '../FileUpload';

const steps = [
    'Your Location',
    'Experience & Skills',
    'Project Preferences',
    'Communication',
    'Identity Verification',
    'Review'
];

// Zimbabwe cities
const zimbabweCities = [
    { value: 'harare', label: 'Harare', province: 'Harare Province' },
    { value: 'bulawayo', label: 'Bulawayo', province: 'Bulawayo Province' },
    { value: 'chitungwiza', label: 'Chitungwiza', province: 'Harare Province' },
    { value: 'mutare', label: 'Mutare', province: 'Manicaland' },
    { value: 'gweru', label: 'Gweru', province: 'Midlands' },
    { value: 'kwekwe', label: 'Kwekwe', province: 'Midlands' },
    { value: 'kadoma', label: 'Kadoma', province: 'Mashonaland West' },
    { value: 'masvingo', label: 'Masvingo', province: 'Masvingo Province' },
    { value: 'chinhoyi', label: 'Chinhoyi', province: 'Mashonaland West' },
    { value: 'marondera', label: 'Marondera', province: 'Mashonaland East' },
    { value: 'norton', label: 'Norton', province: 'Mashonaland West' },
    { value: 'victoria_falls', label: 'Victoria Falls', province: 'Matabeleland North' }
].sort((a, b) => a.label.localeCompare(b.label));

// Countries list (common countries for diaspora)
const countries = [
    { code: 'ZW', name: 'Zimbabwe', currency: 'USD', timezone: 'Africa/Harare', phoneCode: '+263' },
    { code: 'ZA', name: 'South Africa', currency: 'ZAR', timezone: 'Africa/Johannesburg', phoneCode: '+27' },
    { code: 'GB', name: 'United Kingdom', currency: 'GBP', timezone: 'Europe/London', phoneCode: '+44' },
    { code: 'US', name: 'United States', currency: 'USD', timezone: 'America/New_York', phoneCode: '+1' },
    { code: 'AU', name: 'Australia', currency: 'AUD', timezone: 'Australia/Sydney', phoneCode: '+61' },
    { code: 'CA', name: 'Canada', currency: 'CAD', timezone: 'America/Toronto', phoneCode: '+1' },
    { code: 'BW', name: 'Botswana', currency: 'BWP', timezone: 'Africa/Gaborone', phoneCode: '+267' },
    { code: 'MZ', name: 'Mozambique', currency: 'MZN', timezone: 'Africa/Maputo', phoneCode: '+258' },
    { code: 'ZM', name: 'Zambia', currency: 'ZMW', timezone: 'Africa/Lusaka', phoneCode: '+260' },
    { code: 'DE', name: 'Germany', currency: 'EUR', timezone: 'Europe/Berlin', phoneCode: '+49' },
    { code: 'AE', name: 'United Arab Emirates', currency: 'AED', timezone: 'Asia/Dubai', phoneCode: '+971' },
].sort((a, b) => a.name.localeCompare(b.name));

// Project types
const projectTypes = [
    'Residential House',
    'Apartment Building',
    'Commercial Property',
    'Industrial Facility',
    'Renovation/Extension',
    'Land Development',
    'Road Construction',
    'Water Systems'
];

// PM specializations
const pmSpecializations = [
    'Residential Construction',
    'Commercial Construction',
    'Industrial Projects',
    'Renovation & Remodeling',
    'Infrastructure',
    'Multi-family Housing',
    'Custom Homes',
    'Green/Sustainable Building'
];

const ProjectManagerOnboarding = ({ onComplete }) => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        // Location Information
        current_country: '',
        city: '',
        country_code: '',
        phone_country_code: '+263',
        timezone: 'Africa/Harare',
        
        // Experience & Skills
        years_experience: '',
        projects_managed: '',
        specializations: [],
        certifications: '',
        bio: '',
        
        // Project Preferences
        preferred_project_types: [],
        preferred_locations: [],
        min_project_value: '',
        max_project_value: '',
        available_for_remote: false,
        
        // Communication
        phone_number: '',
        preferred_contact_method: 'email',
        communication_frequency: 'weekly',
        whatsapp_notifications: true,
        email_notifications: true,
        sms_notifications: false,
        
        // Identity Verification
        identityDocument: null,
        identityDocumentType: 'national_id',
        identityDocumentUrl: ''
    });

    const [errors, setErrors] = useState({});

    // Handle next step
    const handleNext = () => {
        if (validateStep(activeStep)) {
            if (activeStep === steps.length - 1) {
                handleComplete();
            } else {
                setActiveStep((prevStep) => prevStep + 1);
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    // Validate current step
    const validateStep = (step) => {
        const newErrors = {};
        
        switch (step) {
            case 0: // Location
                if (!formData.country_code) newErrors.country_code = 'Required';
                if (!formData.city || !formData.city.trim()) newErrors.city = 'Please enter your city';
                break;
            case 1: // Experience
                if (!formData.years_experience) newErrors.years_experience = 'Required';
                if (formData.specializations.length === 0) newErrors.specializations = 'Select at least one';
                if (!formData.bio || formData.bio.length < 50) newErrors.bio = 'Please write at least 50 characters';
                break;
            case 2: // Preferences
                if (formData.preferred_project_types.length === 0) newErrors.preferred_project_types = 'Select at least one';
                if (formData.preferred_locations.length === 0) newErrors.preferred_locations = 'Select at least one';
                break;
            case 3: // Communication
                if (!formData.phone_number) newErrors.phone_number = 'Required';
                break;
            case 4: // Identity Verification
                if (!formData.identityDocumentUrl) {
                    newErrors.identityDocument = 'Please upload your National ID, License, or Passport';
                }
                break;
            default:
                break;
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form completion
    const handleComplete = async () => {
        setLoading(true);
        try {
            // Save onboarding data to user profile
            if (updateUser) {
                updateUser(formData);
            }
            
            // Call onComplete callback if provided
            if (onComplete) {
                onComplete(formData);
            }
            
            // Navigate to dashboard
            navigate('/dashboard');
        } catch (error) {
            console.error('Error completing onboarding:', error);
            alert('There was an error saving your profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle country selection
    const handleCountryChange = (countryCode) => {
        const country = countries.find(c => c.code === countryCode);
        if (country) {
            setFormData({
                ...formData,
                country_code: countryCode,
                current_country: country.name,
                timezone: country.timezone,
                phone_country_code: country.phoneCode || '+1'
            });
        }
    };

    // Handle multi-select
    const handleMultiSelect = (field, value) => {
        const current = formData[field] || [];
        const updated = current.includes(value)
            ? current.filter(item => item !== value)
            : [...current, value];
        setFormData({ ...formData, [field]: updated });
    };

    // Render step content
    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Alert severity="info" icon={<SupervisorAccount />} sx={{ mb: 3 }}>
                            <AlertTitle>Welcome, Project Manager!</AlertTitle>
                            Let's set up your profile so clients can find and hire you for their construction projects.
                        </Alert>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.country_code}>
                                    <InputLabel>Current Country of Residence *</InputLabel>
                                    <Select
                                        value={formData.country_code}
                                        onChange={(e) => handleCountryChange(e.target.value)}
                                        label="Current Country of Residence *"
                                    >
                                        {countries.map(country => (
                                            <MenuItem key={country.code} value={country.code}>
                                                {country.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="City *"
                                    value={formData.city}
                                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                                    error={!!errors.city}
                                    helperText={errors.city || "Enter your city name"}
                                    placeholder="Type your city name"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Your Timezone"
                                    value={formData.timezone}
                                    disabled
                                    helperText="Automatically set based on your country"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Engineering color="primary" />
                            Your Experience & Skills
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Tell us about your project management experience
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Years of Experience *"
                                    value={formData.years_experience}
                                    onChange={(e) => setFormData({...formData, years_experience: e.target.value})}
                                    error={!!errors.years_experience}
                                    helperText={errors.years_experience}
                                    inputProps={{ min: 0, max: 50 }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Projects Managed"
                                    value={formData.projects_managed}
                                    onChange={(e) => setFormData({...formData, projects_managed: e.target.value})}
                                    helperText="Approximate number of projects you've managed"
                                    inputProps={{ min: 0, max: 1000 }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Specializations * ({formData.specializations.length} selected)
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {pmSpecializations.map(spec => (
                                        <Chip
                                            key={spec}
                                            label={spec}
                                            onClick={() => handleMultiSelect('specializations', spec)}
                                            color={formData.specializations.includes(spec) ? 'primary' : 'default'}
                                            variant={formData.specializations.includes(spec) ? 'filled' : 'outlined'}
                                        />
                                    ))}
                                </Box>
                                {errors.specializations && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        {errors.specializations}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Certifications"
                                    value={formData.certifications}
                                    onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                                    helperText="Any relevant certifications (PMP, PRINCE2, etc.)"
                                    placeholder="e.g., PMP, PRINCE2, Construction Management Certificate"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Professional Bio *"
                                    value={formData.bio}
                                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                                    error={!!errors.bio}
                                    helperText={errors.bio || `${formData.bio.length}/50 characters minimum`}
                                    placeholder="Describe your experience, approach to project management, and what makes you stand out..."
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business color="primary" />
                            Project Preferences
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            What types of projects are you looking to manage?
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Preferred Project Types * ({formData.preferred_project_types.length} selected)
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {projectTypes.map(type => (
                                        <Chip
                                            key={type}
                                            label={type}
                                            onClick={() => handleMultiSelect('preferred_project_types', type)}
                                            color={formData.preferred_project_types.includes(type) ? 'primary' : 'default'}
                                            variant={formData.preferred_project_types.includes(type) ? 'filled' : 'outlined'}
                                        />
                                    ))}
                                </Box>
                                {errors.preferred_project_types && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        {errors.preferred_project_types}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.preferred_locations}>
                                    <InputLabel>Preferred Locations in Zimbabwe *</InputLabel>
                                    <Select
                                        multiple
                                        value={formData.preferred_locations}
                                        onChange={(e) => setFormData({...formData, preferred_locations: e.target.value})}
                                        label="Preferred Locations in Zimbabwe *"
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map(value => (
                                                    <Chip key={value} label={zimbabweCities.find(c => c.value === value)?.label || value} size="small" />
                                                ))}
                                            </Box>
                                        )}
                                    >
                                        {zimbabweCities.map(city => (
                                            <MenuItem key={city.value} value={city.value}>
                                                <Checkbox checked={formData.preferred_locations.includes(city.value)} />
                                                {city.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Minimum Project Value (USD)"
                                    value={formData.min_project_value}
                                    onChange={(e) => setFormData({...formData, min_project_value: e.target.value})}
                                    InputProps={{
                                        startAdornment: <AttachMoney />
                                    }}
                                    helperText="Leave blank for no minimum"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Maximum Project Value (USD)"
                                    value={formData.max_project_value}
                                    onChange={(e) => setFormData({...formData, max_project_value: e.target.value})}
                                    InputProps={{
                                        startAdornment: <AttachMoney />
                                    }}
                                    helperText="Leave blank for no maximum"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.available_for_remote}
                                            onChange={(e) => setFormData({...formData, available_for_remote: e.target.checked})}
                                        />
                                    }
                                    label="I'm available for remote project management (for diaspora clients)"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 3:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Communication Preferences
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            How would you like clients to reach you?
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        label="Code"
                                        value={formData.phone_country_code}
                                        disabled
                                        sx={{ width: '100px' }}
                                        InputProps={{
                                            startAdornment: <Phone sx={{ mr: 0.5, color: 'action.active' }} />
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Phone Number *"
                                        value={formData.phone_number}
                                        onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                                        error={!!errors.phone_number}
                                        helperText={errors.phone_number || "Enter phone without country code"}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Preferred Contact Method</InputLabel>
                                    <Select
                                        value={formData.preferred_contact_method}
                                        onChange={(e) => setFormData({...formData, preferred_contact_method: e.target.value})}
                                        label="Preferred Contact Method"
                                    >
                                        <MenuItem value="email">📧 Email</MenuItem>
                                        <MenuItem value="whatsapp">💬 WhatsApp</MenuItem>
                                        <MenuItem value="phone">📞 Phone Call</MenuItem>
                                        <MenuItem value="sms">📱 SMS</MenuItem>
                                        <MenuItem value="video">🎥 Video Call</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Update Frequency</InputLabel>
                                    <Select
                                        value={formData.communication_frequency}
                                        onChange={(e) => setFormData({...formData, communication_frequency: e.target.value})}
                                        label="Update Frequency"
                                    >
                                        <MenuItem value="daily">Daily Updates</MenuItem>
                                        <MenuItem value="weekly">Weekly Summaries</MenuItem>
                                        <MenuItem value="biweekly">Bi-weekly Reports</MenuItem>
                                        <MenuItem value="milestones">Milestone Updates Only</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Notification Channels
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.email_notifications}
                                                onChange={(e) => setFormData({...formData, email_notifications: e.target.checked})}
                                            />
                                        }
                                        label="Email Notifications"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.whatsapp_notifications}
                                                onChange={(e) => setFormData({...formData, whatsapp_notifications: e.target.checked})}
                                            />
                                        }
                                        label="WhatsApp Notifications"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.sms_notifications}
                                                onChange={(e) => setFormData({...formData, sms_notifications: e.target.checked})}
                                            />
                                        }
                                        label="SMS Notifications"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 4: // Identity Verification
                return (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <VerifiedUser sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Identity Verification
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Upload your National ID, License, or Passport for verification
                                </Typography>
                            </Box>
                        </Box>

                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                <strong>Required for full access:</strong> Please upload a clear photo or scan of one of the following:
                            </Typography>
                            <List dense sx={{ mt: 1, mb: 0 }}>
                                <ListItem sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                        <CheckCircle color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="National ID Card" />
                                </ListItem>
                                <ListItem sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                        <CheckCircle color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Professional License" />
                                </ListItem>
                                <ListItem sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                        <CheckCircle color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Passport" />
                                </ListItem>
                            </List>
                        </Alert>

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Document Type</InputLabel>
                                    <Select
                                        value={formData.identityDocumentType}
                                        label="Document Type"
                                        onChange={(e) => setFormData({ ...formData, identityDocumentType: e.target.value })}
                                    >
                                        <MenuItem value="national_id">National ID Card</MenuItem>
                                        <MenuItem value="license">Professional License</MenuItem>
                                        <MenuItem value="passport">Passport</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                {formData.identityDocumentUrl ? (
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <CheckCircle color="success" sx={{ fontSize: 40 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" color="success.main" gutterBottom>
                                                    Document Uploaded Successfully
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formData.identityDocumentType === 'national_id' && 'National ID Card'}
                                                    {formData.identityDocumentType === 'license' && 'Professional License'}
                                                    {formData.identityDocumentType === 'passport' && 'Passport'}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setFormData({ ...formData, identityDocumentUrl: '', identityDocument: null });
                                                }}
                                            >
                                                Change
                                            </Button>
                                        </Box>
                                    </Paper>
                                ) : (
                                    <Box>
                                        <FileUpload
                                            uploadType="documents"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            multiple={false}
                                            maxSize={10 * 1024 * 1024}
                                            onUploadComplete={(uploadData) => {
                                                setFormData({
                                                    ...formData,
                                                    identityDocumentUrl: uploadData.file?.url || uploadData.url,
                                                    identityDocument: uploadData
                                                });
                                            }}
                                            additionalData={{
                                                documentType: formData.identityDocumentType,
                                                documentName: formData.identityDocumentType === 'national_id' ? 'National ID' :
                                                             formData.identityDocumentType === 'license' ? 'Professional License' : 'Passport',
                                                documentCategory: 'identity'
                                            }}
                                            buttonText="Upload Document"
                                        />
                                    </Box>
                                )}
                            </Grid>

                            {errors.identityDocument && (
                                <Grid item xs={12}>
                                    <Alert severity="error">{errors.identityDocument}</Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                );

            case 5: // Review
                return (
                    <Box>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'success.main' }}>
                                <CheckCircle sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Typography variant="h5" gutterBottom>
                                You're Almost Ready!
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Review your information and complete your profile
                            </Typography>
                        </Box>

                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Your Location
                            </Typography>
                            <Typography variant="body2">
                                {formData.current_country}, {formData.city}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Timezone: {formData.timezone}
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Experience
                            </Typography>
                            <Typography variant="body2">
                                {formData.years_experience} years experience • {formData.projects_managed || '0'} projects managed
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {formData.specializations.map(spec => (
                                    <Chip key={spec} label={spec} size="small" />
                                ))}
                            </Box>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Project Preferences
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                Types: {formData.preferred_project_types.join(', ')}
                            </Typography>
                            <Typography variant="body2">
                                Locations: {formData.preferred_locations.map(loc => 
                                    zimbabweCities.find(c => c.value === loc)?.label || loc
                                ).join(', ')}
                            </Typography>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Communication
                            </Typography>
                            <Typography variant="body2">
                                {formData.phone_country_code} {formData.phone_number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Preferred: {formData.preferred_contact_method} • Updates: {formData.communication_frequency}
                            </Typography>
                        </Paper>

                        {formData.identityDocumentUrl && (
                            <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50', borderColor: 'success.main' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <VerifiedUser color="success" sx={{ fontSize: 32 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" color="success.main" gutterBottom>
                                            Identity Document Verified
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formData.identityDocumentType === 'national_id' && 'National ID Card'}
                                            {formData.identityDocumentType === 'license' && 'Professional License'}
                                            {formData.identityDocumentType === 'passport' && 'Passport'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        <Alert severity="success" icon={<Security />}>
                            <AlertTitle>What Happens Next?</AlertTitle>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircle fontSize="small" color="success" /></ListItemIcon>
                                    <ListItemText primary="Your profile will be created" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle fontSize="small" color="success" /></ListItemIcon>
                                    <ListItemText primary="Clients can find and contact you for projects" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircle fontSize="small" color="success" /></ListItemIcon>
                                    <ListItemText primary="You can start managing projects on Eqaya" />
                                </ListItem>
                            </List>
                        </Alert>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    Welcome to Eqaya
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Let's set up your project manager profile
                </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Content */}
            <Card elevation={3}>
                <CardContent sx={{ p: 4 }}>
                    {renderStepContent(activeStep)}
                </CardContent>
            </Card>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    disabled={activeStep === 0 || loading}
                    onClick={handleBack}
                    size="large"
                >
                    Back
                </Button>
                <Box>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleComplete}
                            disabled={loading}
                            sx={{ minWidth: 200 }}
                        >
                            {loading ? <LinearProgress sx={{ width: '100%' }} /> : 'Complete Setup'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleNext}
                            sx={{ minWidth: 150 }}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ProjectManagerOnboarding;








